import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { AuthRepository } from "@/api/auth/repository";
import { AuthService } from "@/api/auth/service";
import type { ISignupPasswordResponse } from "@/api/auth/types";
import { InputPasswordControl } from "@/components/controls";
import { ROUTES } from "@/router";
import { Button, Message } from "@/ui-kit";
import { getAuthErrorKey, handleSuccessfulAuthentication } from "../../utils";
import { FormView } from "../FormView";
import { PasswordRequirementMessage } from "../PasswordRequirementMessage";

type IForm = {
  password: string;
  repeat_password: string;
};

const SignUpPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  const authRepository = useMemo(() => {
    return new AuthRepository(new AuthService());
  }, []);

  const schema = useMemo(() => {
    return z
      .object({
        password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_length") }),
        repeat_password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_length") }),
      })
      .refine((data) => data.password === data.repeat_password, {
        message: t("validation.passwords_must_match"),
        path: ["repeat_password"],
      });
  }, [t]);

  const form = useForm<IForm>({
    defaultValues: {
      password: "",
      repeat_password: "",
    },
    resolver: zodResolver(schema),
  });
  const { handleSubmit, formState, watch } = form;
  const password = watch("password");

  const onSubmit = useCallback(
    (formData: IForm) => {
      setLoading(true);
      return authRepository
        .signupPassword({
          password: formData.password,
        })
        .then((response: ISignupPasswordResponse) => {
          return handleSuccessfulAuthentication(
            response.accessToken,
            response.refreshToken,
          );
        })
        .catch((e: { suberror: string }) => {
          setMessage(t(getAuthErrorKey(e?.suberror)));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [authRepository, t],
  );

  const handleBackClick = useCallback(() => {
    navigate(`/${ROUTES.AUTH}/${ROUTES.SIGN_UP}`);
  }, [navigate]);

  return (
    <FormProvider {...form}>
      <FormView title={t("label.createPassword")}>
        <form
          className="nuco-auth__common-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <InputPasswordControl
            name="password"
            size="lg"
            label={t("label.password")}
            placeholder={t("placeholder.enterPassword")}
            autoFocus={true}
            invalid={!!formState.errors.password}
          />

          <InputPasswordControl
            name="repeat_password"
            size="lg"
            label={t("label.repeat_password")}
            placeholder={t("placeholder.enterPassword")}
            invalid={!!formState.errors.repeat_password}
          />

          {message && (
            <div className="nuco-auth-common-form__message">
              <Message text={message} color="danger" />
            </div>
          )}

          <PasswordRequirementMessage password={password} />

          <div className="nuco-auth__common-form-actions">
            <Button
              type="submit"
              size="lg"
              content={t("action.continue")}
              loading={loading}
            />

            <Button
              variant="soft"
              type="button"
              size="lg"
              color="neutral"
              content={t("action.back")}
              onClick={handleBackClick}
            />
          </div>
        </form>
      </FormView>
    </FormProvider>
  );
};

export default SignUpPassword;
